// Configuração da API
// Em produção, SEMPRE usa a mesma origem do site (requer proxy /api/v1 no servidor)
// Em desenvolvimento, usa VITE_API_BASE_URL se definida, senão localhost:3001
export const API_BASE_URL = import.meta.env.PROD
  ? `${window.location.origin}/api/v1`
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1');

// Função helper para fazer requisições
import { logEvent, logError } from "@/lib/logger";

const CSRF_STORAGE_KEY = "mr_csrf_token";

const decodeJwtExp = (token: string) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof decoded?.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const isCsrfValid = (token: string) => {
  const exp = decodeJwtExp(token);
  if (!exp) return false;
  return Date.now() < exp - 30_000;
};

const fetchCsrfToken = async () => {
  const response = await fetch(`${API_BASE_URL}/auth/csrf`, {
    method: "GET",
    credentials: "include"
  });
  if (!response.ok) return "";
  try {
    const data = await response.json();
    return data?.token || "";
  } catch {
    return "";
  }
};

const saveCsrfToken = (token: string) => {
  if (!token) return;
  try {
    localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {}
};

const getCsrfToken = async () => {
  try {
    const stored = localStorage.getItem(CSRF_STORAGE_KEY);
    if (stored && isCsrfValid(stored)) return stored;
  } catch {}
  const token = await fetchCsrfToken();
  saveCsrfToken(token);
  return token;
};

const isCsrfError = (status: number, payload: unknown) => {
  if (status !== 403) return false;
  if (typeof payload === "string") return /csrf/i.test(payload);
  try {
    return /csrf/i.test(JSON.stringify(payload || {}));
  } catch {
    return false;
  }
};

const readErrorPayload = async (response: Response) => {
  try {
    return await response.clone().json();
  } catch {
    try {
      return await response.clone().text();
    } catch {
      return null;
    }
  }
};

const isHtmlPayload = (value: unknown) =>
  typeof value === "string" && /<html[\s>]/i.test(value);

const buildStatusMessage = (response: Response) => {
  switch (response.status) {
    case 400:
      return "Dados invalidos. Revise as informacoes e tente novamente.";
    case 401:
      return "Credenciais invalidas ou sessao expirada.";
    case 403:
      return "Sua sessao expirou ou a solicitacao foi bloqueada. Tente novamente.";
    case 404:
      return "Servico de autenticacao indisponivel no momento.";
    case 429:
      return "Muitas tentativas. Aguarde alguns instantes antes de tentar novamente.";
    case 500:
    case 502:
    case 503:
    case 504:
      return "Servico temporariamente indisponivel. Tente novamente em instantes.";
    default:
      return `API Error: ${response.status} ${response.statusText}`.trim();
  }
};

const extractErrorMessage = (response: Response, errorPayload: unknown) => {
  if (typeof errorPayload === "string" && errorPayload.trim() && !isHtmlPayload(errorPayload)) {
    return errorPayload;
  }

  if (errorPayload && typeof errorPayload === "object") {
    const payload = errorPayload as Record<string, unknown>;

    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }

    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }

    if (Array.isArray(payload.errors) && payload.errors.length > 0) {
      const firstError = payload.errors[0] as Record<string, unknown> | undefined;
      if (typeof firstError?.message === "string" && firstError.message.trim()) {
        return firstError.message;
      }
    }
  }

  return buildStatusMessage(response);
};

const notifyExpiredSession = (endpoint: string) => {
  if (endpoint.startsWith("/auth/")) return;
  try {
    if (!localStorage.getItem("token")) return;
    const redirectTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem(CSRF_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("mr:session-expired", {
      detail: { redirectTo },
    }));
  } catch {}
};

export class ApiRequestError extends Error {
  status?: number;
  payload?: unknown;
  url?: string;
  isNetworkError?: boolean;

  constructor(message: string, details?: Partial<ApiRequestError>) {
    super(message);
    this.name = "ApiRequestError";
    Object.assign(this, details);
  }
}

export const getApiErrorMessage = (error: unknown, fallback = "Nao foi possivel concluir a solicitacao.") => {
  if (error instanceof ApiRequestError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  try { logEvent('api_request', { url, method: options.method || 'GET' }); } catch {}
  
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const method = (options.method || 'GET').toUpperCase();
  const requiresCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  const csrfToken = requiresCsrf ? await getCsrfToken() : "";
  const buildHeaders = (token?: string) => ({
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { 'X-CSRF-Token': token } : {}),
    ...(options.headers || {}),
  });
  // Importante: primeiro espalhar options e DEPOIS mesclar headers,
  // para evitar que options sobrescreva completamente o objeto headers.
  const defaultOptions: RequestInit = {
    ...options,
    credentials: 'include',
    headers: buildHeaders(csrfToken),
  };

  try {
    let response = await fetch(url, defaultOptions);
    let errorPayload: any = null;
    if (!response.ok) {
      errorPayload = await readErrorPayload(response);

      if (requiresCsrf && isCsrfError(response.status, errorPayload)) {
        const freshToken = await fetchCsrfToken();
        if (freshToken) {
          saveCsrfToken(freshToken);
          const retryOptions: RequestInit = {
            ...defaultOptions,
            headers: buildHeaders(freshToken),
          };
          response = await fetch(url, retryOptions);
          if (response.ok) {
            try { logEvent('api_response_ok_after_csrf_retry', { url, status: response.status }); } catch {}
            return response;
          }
          errorPayload = await readErrorPayload(response);
        }
      }

      try { logError('api_response_error', { url, status: response.status, statusText: response.statusText, errorPayload }); } catch {}
      if (response.status === 401) {
        notifyExpiredSession(endpoint);
      }
      const message = extractErrorMessage(response, errorPayload);
      const error = new ApiRequestError(message, {
        status: response.status,
        payload: errorPayload,
        url
      });
      throw error;
    }
    try { logEvent('api_response_ok', { url, status: response.status }); } catch {}
    return response;
  } catch (error: unknown) {
    if (error instanceof ApiRequestError) {
      throw error;
    }
    const wrappedError = new ApiRequestError(
      "Falha de conexao. Verifique sua internet e tente novamente.",
      {
        url,
        isNetworkError: true,
        payload: error instanceof Error ? error.message : String(error)
      }
    );
    try { logError('fetch_error', { url, error: String(error) }); } catch {}
    throw wrappedError;
  }
};
